// Runtime configuration interface
interface RuntimeConfig {
  API_BASE_URL: string;
  API_TIMEOUT: number;
  COGNITO_USER_POOL_ID: string;
  COGNITO_CLIENT_ID: string;
  COGNITO_REGION: string;
  NODE_ENV: string;
  WS_URL?: string;
}

// Global runtime configuration
let runtimeConfig: RuntimeConfig | null = null;

// Load runtime configuration from /config.json
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (runtimeConfig) {
    return runtimeConfig;
  }

  try {
    const response = await fetch('/config.json');
    if (!response.ok) {
      throw new Error(`Failed to load config: ${response.status}`);
    }
    runtimeConfig = await response.json();
    return runtimeConfig;
  } catch (error) {
    console.warn('Failed to load runtime config, falling back to environment variables:', error);
    // Fallback to environment variables for development
    runtimeConfig = {
      API_BASE_URL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
      API_TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
      COGNITO_USER_POOL_ID: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
      COGNITO_CLIENT_ID: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
      COGNITO_REGION: import.meta.env.VITE_COGNITO_REGION || 'ap-southeast-2',
      NODE_ENV: import.meta.env.VITE_NODE_ENV || 'development',
      WS_URL: import.meta.env.VITE_WS_URL,
    };
    return runtimeConfig;
  }
}

// Environment configuration - combines runtime config with build-time static config
export const env = {
  // Static configuration (available at build time)
  STRIPE_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
  
  // Dynamic getters for runtime configuration
  get API_BASE_URL() {
    return runtimeConfig?.API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
  },
  get API_TIMEOUT() {
    return runtimeConfig?.API_TIMEOUT || parseInt(import.meta.env.VITE_API_TIMEOUT || '30000');
  },
  get COGNITO_USER_POOL_ID() {
    return runtimeConfig?.COGNITO_USER_POOL_ID || import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
  },
  get COGNITO_CLIENT_ID() {
    return runtimeConfig?.COGNITO_CLIENT_ID || import.meta.env.VITE_COGNITO_CLIENT_ID || '';
  },
  get COGNITO_REGION() {
    return runtimeConfig?.COGNITO_REGION || import.meta.env.VITE_COGNITO_REGION || 'ap-southeast-2';
  },
  get NODE_ENV() {
    return runtimeConfig?.NODE_ENV || import.meta.env.VITE_NODE_ENV || 'development';
  },
  get WS_URL() {
    return runtimeConfig?.WS_URL || import.meta.env.VITE_WS_URL || '';
  },
  get IS_DEVELOPMENT() {
    return this.NODE_ENV === 'development';
  },
  get IS_PRODUCTION() {
    return this.NODE_ENV === 'production';
  },
} as const;

// Validate required configuration (both runtime and build-time)
export function validateEnvironment(): { valid: boolean; missing: string[] } {
  const missing: string[] = [];
  
  // Check runtime configuration
  if (!env.API_BASE_URL) missing.push('API_BASE_URL');
  if (!env.COGNITO_USER_POOL_ID) missing.push('COGNITO_USER_POOL_ID');
  if (!env.COGNITO_CLIENT_ID) missing.push('COGNITO_CLIENT_ID');
  
  // Check build-time configuration
  if (!env.STRIPE_PUBLISHABLE_KEY) missing.push('STRIPE_PUBLISHABLE_KEY');
  
  return {
    valid: missing.length === 0,
    missing,
  };
}

// Check if runtime configuration is loaded
export function isRuntimeConfigLoaded(): boolean {
  return runtimeConfig !== null;
}
